import {useTranslation} from 'react-i18next';

import {Box, Chip, Typography} from '@mui/material';

import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {alignmentStatus, orangeColors} from '../../../../../layouts/theme/designSystem';
import {getLocalisedPropertyValue} from '../../../../../utils/getLocalisedPropertyValue';
import {WorkforceProfileVersionEvolutionSkillRef} from '../../types/WorkforceProfileTypes';

export interface Props {
  title: string;
  items: WorkforceProfileVersionEvolutionSkillRef[];
  language: 'en' | 'de';
  variant: 'positive' | 'warning' | 'negative';
  showCount?: boolean;
}

export const WorkforceProfileVersionEvolutionSkillList = ({
  title,
  items,
  language,
  variant,
  showCount = false,
}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceProfilesPage.detail.versionEvolution',
  });

  if (items.length === 0) {
    return null;
  }

  const chipColors =
    variant === 'positive'
      ? {bgcolor: alignmentStatus.metChipBg, color: alignmentStatus.metChipFg}
      : variant === 'warning'
        ? {bgcolor: alignmentStatus.warningChipBg, color: alignmentStatus.warningChipFg}
        : {bgcolor: alignmentStatus.gapChipBg, color: alignmentStatus.gapChipFg};

  return (
    <EmpionPaper
      sx={{
        border: `1px solid ${orangeColors[3]}`,
        borderRadius: '14px',
        p: 2.5,
        mb: 2,
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap'}}>
        <Typography variant='subtitle2' sx={{fontWeight: 600}}>
          {title}
        </Typography>
        {showCount && (
          <Chip
            label={items.length}
            size='small'
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: orangeColors[5],
              color: 'text.secondary',
            }}
          />
        )}
      </Box>
      {items.map((item, index) => (
        <Box
          key={`${item.section}-${getLocalisedPropertyValue(item.skill, language)}-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            py: 0.75,
            borderBottom: `0.5px solid ${orangeColors[3]}`,
            '&:last-child': {borderBottom: 'none', pb: 0},
          }}
        >
          <Typography variant='body2' sx={{flex: 1, minWidth: 160}}>
            {getLocalisedPropertyValue(item.skill, language)}
          </Typography>
          <Chip
            label={t(`sections.${item.section}`, {defaultValue: item.section})}
            size='small'
            sx={{height: 22, fontSize: 11, fontWeight: 500, ...chipColors}}
          />
        </Box>
      ))}
    </EmpionPaper>
  );
};
